// apps/codeframe/src/motion/scrollDriver.ts
import { scroll, inView } from 'motion';
import type { PlaybackConfig } from '../store/types';
import { resolveCfNode } from '../store/slot-schema';
import type { MotionPlayer } from './player';

type ScrollCfg = NonNullable<PlaybackConfig['scroll']>;

const clamp01 = (v: number) => Math.max(0, Math.min(1, v));

/** Pure: map a 0..1 scroll progress through `range` then onto `maps` (ms). */
export function scrollProgressToTime(progress: number, cfg: ScrollCfg): number {
  const [r0, r1] = cfg.range;
  const span = r1 - r0;
  const local = span === 0 ? 0 : clamp01((progress - r0) / span);
  const [m0, m1] = cfg.maps;
  return Math.round(m0 + (m1 - m0) * local);
}

/**
 * Subscribe a player's time to scroll/in-view progress. Returns a cleanup fn.
 * `source: 'frameInView'` → the frame root is the scroll target; a cfId string
 * targets that element. `scroll()` drives the time; `inView()` is advisory.
 */
export function wireScroll(player: MotionPlayer, root: HTMLElement, cfg: ScrollCfg): () => void {
  const target =
    cfg.source === 'frameInView'
      ? root
      : (resolveCfNode(root, cfg.source) as HTMLElement | null) ?? root;

  const stopScroll = scroll(
    (progress: number) => {
      player.seek(scrollProgressToTime(progress, cfg));
    },
    { target },
  );

  const stopInView = inView(target, () => {
    // advisory only — scroll() supplies the driving progress
    return () => {};
  });

  return () => {
    stopScroll();
    stopInView();
  };
}
